import React, { useState, useEffect } from 'react'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import { Map, List } from 'immutable'
import _toLower from 'lodash/toLower'
import _get from 'lodash/get'
import { H6 } from '../../Heading/index'
import Button, { TYPES } from '../../Button/index'
import { connect as connectStaticText } from '../../StaticText/connect'
import { getBoundActions } from '../../../actions/index'
import {
  ACCOUNT_TYPE_MYQEE,
  isTaxable,
} from '../../../services/user-account/index'
import { TYPE_CART_CHANGE_PLAN } from '../../../services/dialog/index'
import {
  PLAN_SUBSCRIPTION_MONTHLY,
  PLAN_SKU_LIVE,
  formatPrice,
  getFreeTrialPeriod,
  getPlanSubscriptionType,
} from '../../../services/plans/index'
import IconV2, { ICON_TYPES } from '../../Icon.v2/index'
import {
  getCurrentTime,
  getFormattedLocaleDate,
  formatDateWithTimezonOffset,
  dateIsFuture,
  addDays,
} from '../../../services/date-time/index'
import { getPrimary } from '../../../services/languages/index'
import { GIFT_SUBSCRIPTION_STATE } from '../../../services/gift/index'
import { CAD, EUR } from '../../../services/currency/index'
import { useUpdateSelectedPlan, useGetPlanGridData } from '../../../services/plans/hooks'
import { get as getConfig } from '../../../config/index'
import { FR } from '../../../services/languages/constants'
import PlanValueProp from '../../PlanValueProp/index'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

function CartOrderSummaryV2 (props) {
  const {
    plans,
    auth,
    user,
    staticText,
    footer,
    userAccount,
    planIsPreselected,
    setPlansSelection,
  } = props

  // All hooks must be called at the top level, before any conditional logic
  const [isClient, setIsClient] = useState(false)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  // Call hooks conditionally but always in the same order
  const planGridData = planGraphQLActive ? useGetPlanGridData({
    userLanguage: getPrimary(user.getIn(['data', 'language'])),
    authJwt: auth.get('jwt'),
  }) || List() : List()

  // Always call useUpdateSelectedPlan, but it can handle the conditional logic internally
  useUpdateSelectedPlan({
    authJwt: auth.get('jwt'),
    userLanguage: getPrimary(user.getIn(['data', 'language'])),
    selectedPlan: plans.get('selection', Map()) || Map(),
    setPlansSelection,
    userAccountCurrencyIso: userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'currencyIso']),
  })

  // Early returns after all hooks are called
  if (!isClient) {
    return null
  }

  const selectedPlan = plans.get('selection', Map()) || Map()
  if (!selectedPlan || !Map.isMap(selectedPlan) || selectedPlan.size === 0) {
    return null
  }

  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  let selectedPlanInPlanGridData

  // Set selectedPlanInPlanGridData based on the planGridData
  if (planGraphQLActive) {
    selectedPlanInPlanGridData = planGridData.find((plan) => plan.get('sku') === selectedPlan.get('sku'))
  }

  const onClickChangePlan = () => {
    const { renderModal } = props
    renderModal(TYPE_CART_CHANGE_PLAN)
  }

  const selectedPlanSku = plans.getIn(['selection', 'sku'])
  const planFreeTrialPeriod = getFreeTrialPeriod(plans.getIn(['selection', 'segments'], List()))
  const formattedDate = new Date(addDays(getCurrentTime(), planFreeTrialPeriod))
  const giftSubscriptionActive = userAccount.getIn(['details', 'data', 'gift', 'status']) === GIFT_SUBSCRIPTION_STATE.ACTIVE
  const giftPaidThroughDate = userAccount.getIn(['details', 'data', 'gift', 'paidThroughDate'])
  const expireDate = getFormattedLocaleDate(
    giftSubscriptionActive ? giftPaidThroughDate : formatDateWithTimezonOffset(formattedDate),
    userLanguage,
  )
  const preTax = isTaxable(props) ? ` (${staticText.getIn(['data', 'preTax'])})` : null

  // start myqee
  const userAcquisition = user.get('userAcquisitions', List()).first() || Map()
  const userAcquisitionSource = userAcquisition.get('source')
  const isMyqeeUser = userAcquisitionSource === ACCOUNT_TYPE_MYQEE
  const myqeePaidThroughDate = userAcquisition.get('paidThroughDate')
  const myqeePaidThroughDateInFuture = myqeePaidThroughDate && dateIsFuture(myqeePaidThroughDate)
  const showMyqee = isMyqeeUser && myqeePaidThroughDateInFuture

  // selectedPlan data
  const initialPrice = selectedPlan.get('costs').first()
  const planPrice = selectedPlan.get('costs').last()
  const currencyIso = selectedPlan.get('currencyIso')
  let showCurrencyIso = true

  if (currencyIso === EUR || (currencyIso === CAD && userLanguage === FR)) {
    showCurrencyIso = false
  }

  const allowPlanSwitch = selectedPlanInPlanGridData && !planIsPreselected
  const formattedPlanPrice = `${formatPrice(
    currencyIso,
    planPrice,
    showCurrencyIso,
    null,
    userLanguage,
  )}${preTax || ''}`
  const showFormattedPlanPrice = planFreeTrialPeriod
    || myqeePaidThroughDateInFuture
    || giftSubscriptionActive
  const totalDueNow = showFormattedPlanPrice
    ? formatPrice(currencyIso, '0', showCurrencyIso, null, userLanguage)
    : `${formatPrice(currencyIso, initialPrice, showCurrencyIso, null, userLanguage)}${preTax || ''}`
  let planName = selectedPlan.get('name') || selectedPlan.get('heading')
  let planDescription = selectedPlan.get('shortDetails')
  let valueProps = selectedPlan.get('valueProps', List())
  let totalDueOn = `${staticText.getIn(['data', 'totalDueOn'])} ${expireDate}`

  // myqee
  let myqeeEventsPlus = null
  if (showMyqee) {
    const selectionPlanSubscriptionType = getPlanSubscriptionType(plans.getIn(['selection', 'id']))
    const selectionPlanCostPeriod = selectionPlanSubscriptionType === PLAN_SUBSCRIPTION_MONTHLY
      ? staticText.getIn(['data', 'month']) : staticText.getIn(['data', 'year'])
    const myqeeFutureCost = `${formattedPlanPrice}/${selectionPlanCostPeriod}`
    const myqeeDueDate = getFormattedLocaleDate(myqeePaidThroughDate, userLanguage)
    myqeeEventsPlus = selectedPlanSku === PLAN_SKU_LIVE
      ? `${staticText.getIn(['data', 'myqeeEventsPlus'])} ${myqeeDueDate}.`
      : null
    planName = staticText.getIn(['data', _toLower(selectedPlan.get('heading'))])
    planDescription = selectedPlanSku === PLAN_SKU_LIVE ? `${myqeeFutureCost}*` : myqeeFutureCost
    valueProps = valueProps.filter((vp) => !(_toLower(vp)).includes(staticText.getIn(['data', 'trial'])))
    totalDueOn = `${staticText.getIn(['data', 'totalDueOn'])} ${myqeeDueDate}`
  }
  // end myqee

  const onClickShowDetails = () => {
    setExpanded(!expanded)
  }

  const getExpandedClassName = (className, isExpanded) => {
    const baseClass = [className]

    if (isExpanded) {
      baseClass.push(`${className}--expanded`)
    }

    return baseClass.join(' ')
  }

  const renderCartOrderSummaryFooter = () => {
    return (
      <React.Fragment>
        {showFormattedPlanPrice && (
          <div className="cart-order-summary-v2__footer-info">
            <div className="cart-order-summary-v2__footer-label">
              {totalDueOn}
            </div>
            <div className="cart-order-summary-v2__footer-price">
              {`${formattedPlanPrice}`}
            </div>
          </div>
        )}
        <div className="cart-order-summary-v2__footer-info cart-order-summary-v2__footer-info--last">
          <div className="cart-order-summary-v2__footer-label">
          {staticText.getIn(['data', 'totalDueNow'])}
          </div>
          <div className="cart-order-summary-v2__footer-price">
            {totalDueNow}
          </div>
        </div>
        {myqeeEventsPlus && (
          <div className="cart-order-summary-v2__footer-info">
            {myqeeEventsPlus}
          </div>
        )}
      </React.Fragment>
    )
  }

  return (
    <div className="cart-order-summary-v2">
      <div className="cart-order-summary-v2__header">
        <H6 className="cart-order-summary-v2__title">
          <span className="cart-order-summary-v2__title-description">
            {staticText.getIn(['data', 'title'])}
          </span>
        </H6>
        {
          allowPlanSwitch && !footer
            ? <Button
              type={TYPES.LINK}
              onClick={onClickChangePlan}
              className="cart-order-summary-v2__change-plan"
            >
              {staticText.getIn(['data', 'change'])}
            </Button>
            : null
        }
      </div>
      <div className="cart-order-summary-v2__content">
        <div className="cart-order-summary-v2__content-header">
          <div className="cart-order-summary-v2__plan-name">
            {planName}
          </div>
          <div className="cart-order-summary-v2__plan-description">
            {planDescription}
          </div>
        </div>
        <div className="cart-order-summary-v2__show-hide">
          <Button
            type={TYPES.LINK}
            onClick={onClickShowDetails}
            className="cart-order-summary-v2__show-hide-link"
          >
            <span className="cart-order-summary-v2__show-hide-text">
              {expanded ? staticText.getIn(['data', 'hideDetails']) : staticText.getIn(['data', 'seeDetails'])}
            </span>
            <IconV2
              className={getExpandedClassName('cart-order-summary-v2__show-hide-icon', expanded)}
              type={ICON_TYPES.CHEVRON_DOWN}
            />
          </Button>
          <ul className={getExpandedClassName('cart-order-summary-v2__value-props', expanded)}>
            {valueProps.map((item, index) => {
              const row = index
              return (
                <li
                  key={`value-prop-${row}`}
                  className="cart-order-summary-v2__value-item"
                >
                  <PlanValueProp valueProp={item} className="cart-order-summary-v2__value-item-text" />
                </li>
              )
            })}
          </ul>
        </div>
        <div className="cart-order-summary-v2__sep" />

        <div className="cart-order-summary-v2__footer">
          {footer || renderCartOrderSummaryFooter()}
        </div>
      </div>
    </div>
  )
}

CartOrderSummaryV2.propTypes = {
  plans: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  renderModal: PropTypes.func.isRequired,
  footer: PropTypes.element,
  planIsPreselected: PropTypes.bool,
  setPlansSelection: PropTypes.func,
}

CartOrderSummaryV2.defaultProps = {
  footer: null,
}

export default compose(
  connectStaticText({ storeKey: 'cartOrderSummaryV2' }),
  connectRedux(
    (state) => ({
      auth: state.auth,
      plans: state.plans,
      user: state.user,
      userAccount: state.userAccount,
      planIsPreselected: state.checkout.get('checkoutPlanPreselected', false),
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        renderModal: actions.dialog.renderModal,
        setPlansSelection: actions.plans.setPlansSelection,
      }
    },
  ),
)(CartOrderSummaryV2)
